import { draftTask, type PatientDto, type PractitionerDto, usePatients, usePractitioners } from "@repo/data-store";
import { asPatientId } from "@repo/domain";
import { useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { z } from "zod";
import { useAppForm } from "@/shared/forms/app-form";
import { useSubmitOnce } from "@/shared/forms/submit-once";
import { useCurrentPractitioner } from "@/shared/hooks/use-current-practitioner";

// === Schema & Types ===

export const taskFormSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string(),
  status: z.string(),
  priority: z.string(),
  patientId: z.string(),
  practitionerId: z.string(),
  dueDate: z.date().or(z.null()),
});

export type TaskFormValues = z.infer<typeof taskFormSchema>;

export const DEFAULT_TASK_FORM_VALUES: TaskFormValues = {
  title: "",
  description: "",
  status: "draft",
  priority: "routine",
  patientId: "",
  practitionerId: "",
  dueDate: null,
};

// === Hook ===

interface UseTaskFormReturn {
  defaultValues: TaskFormValues;
  validators: { onDynamic: typeof taskFormSchema };
  handleSubmit: (value: TaskFormValues) => void;
  isPending: boolean;
  patients: PatientDto[];
  practitioners: PractitionerDto[];
  isLoadingPatients: boolean;
  isLoadingPractitioners: boolean;
}

interface UseTaskFormDataOptions {
  onSuccess?: () => void;
}

export function useTaskFormData(opts?: UseTaskFormDataOptions): UseTaskFormReturn {
  const [isPending, setIsPending] = useState(false);
  const { once } = useSubmitOnce();
  const { patients, isLoading: isLoadingPatients } = usePatients();
  const { practitioners, isLoading: isLoadingPractitioners } = usePractitioners();
  const { practitioner: currentPractitioner } = useCurrentPractitioner();
  const navigate = useNavigate();

  const defaultValues = useMemo<TaskFormValues>(
    () => ({
      ...DEFAULT_TASK_FORM_VALUES,
      practitionerId: currentPractitioner?.practitionerId ?? "",
    }),
    [currentPractitioner?.practitionerId],
  );

  const handleSubmit = (value: TaskFormValues) => {
    const fullDescription = value.title.trim() + (value.description ? `\n\n${value.description}` : "");
    const ownerId = practitioners.find(
      (practitioner) => practitioner.practitionerId === value.practitionerId,
    )?.practitionerId;

    once((entityId) => {
      setIsPending(true);
      return draftTask({
        id: entityId,
        description: fullDescription,
        status: value.status === "ready" || value.status === "draft" ? value.status : "draft",
        priority: value.priority as "routine" | "urgent" | "asap" | "stat",
        ...(value.patientId ? { patient: { resourceType: "Patient" as const, id: asPatientId(value.patientId) } } : {}),
        ...(ownerId ? { owner: { resourceType: "Practitioner" as const, id: ownerId } } : {}),
        ...(value.dueDate && { dueDate: value.dueDate.toISOString().split("T")[0] }),
      })
        .isPersisted.promise.then(() => {
          if (opts?.onSuccess) {
            opts.onSuccess();
          } else {
            navigate({ to: "/tasks" });
          }
        })
        .finally(() => setIsPending(false));
    })?.catch(() => {});
  };

  return {
    defaultValues,
    validators: { onDynamic: taskFormSchema },
    handleSubmit,
    isPending,
    patients: patients ?? [],
    practitioners: practitioners ?? [],
    isLoadingPatients,
    isLoadingPractitioners,
  };
}

export { useAppForm };
